import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import MaterialLink from '@material-ui/core/Link';
import * as Store from '@apply/store';
import { Header } from './Header';
import { alertModal } from '@apply/ui';
import { applicationInsights } from './ApplicationInsights';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { FeatureType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

const reactPlugin = new ReactPlugin();

export const appInsightsApply = import.meta.env.VITE_APPINSIGHTS_CLIENT_APPLY;
const ai = new ApplicationInsights({
    config: {
        instrumentationKey: appInsightsApply,
        enableAutoRouteTracking: true,
        extensions: [reactPlugin],
    },
});
ai.loadAppInsights();

export type AuthenticationHandler = (
    email: string,
    password: string,
    ipaddress: string
) => Promise<Store.UserStore.Types.Authentication>;

export type UseAuthenticationResult = {
    login: (username: string, password: string, ipaddress: string, handler: AuthenticationHandler) => Promise<boolean>;
    logout: () => void;
    isAuthenticated: boolean;
    isRegisteredUser: boolean;
    authentication: Store.UserStore.Types.Authentication;
    isLocEnabled: boolean;
    requiresESign: boolean;
    requiresAdminTermsESign: boolean;
    isFeatureEnabled: (featureType: FeatureType) => boolean;
};

export const useAuthentication = (): UseAuthenticationResult => {
    const [authentication, setAuthentication] = useState<Store.UserStore.Types.Authentication>();
    const isAuthenticated = useMemo(() => Store.UserStore.Types.isAuthenticated(authentication), [authentication]);
    const isRegisteredUser = useMemo(() => Store.UserStore.Types.isRegisteredUser(authentication), [authentication]);
    const isLocEnabled = useMemo(() => Store.UserStore.Types.isLocEnabled(authentication), [authentication]);
    const requiresESign = useMemo(() => Store.UserStore.Types.requiresESign(authentication), [authentication]);
    const mounted = useRef<boolean>(false);
    const isFeatureEnabled = (featureType: FeatureType) => {
        const auth = Store.UserStore.Types.retrieve();
        return Store.UserStore.Types.isFeatureEnabled(auth.user, featureType);
    };

    useEffect(() => {
        /* istanbul ignore else */
        if (!mounted.current) {
            mounted.current = true;
            const stored = Store.UserStore.Types.retrieve();
            if (stored) {
                setAuthentication(stored);
            }
        }
    }, [mounted]);

    const login = useCallback(
        async (myUsername: string, password: string, ipaddress: string, handler: AuthenticationHandler): Promise<boolean> => {
            if (!myUsername || !password || !handler) {
                return false;
            }

            try {
                const result = await handler(myUsername, password, ipaddress);
                if (!result) {
                    return false;
                }

                Store.UserStore.Types.store(result);
                setAuthentication(result);
                return true;
            } catch (err) {
                return false;
            }
        },
        []
    );

    const logout = useCallback(() => {
        const stackTrace = Error().stack;
        ai.appInsights.trackTrace({ message: 'logout' }, { stackTrace });
        Store.UserStore.Types.clear();
        window.sessionStorage.clear();
        setAuthentication(undefined);

        if (window.location.origin + '/login' !== window.location.href) {
            window.location.href = '/login';
        }
    }, []);

    return {
        login,
        logout,
        isAuthenticated,
        isRegisteredUser,
        authentication,
        isLocEnabled,
        requiresESign,
        isFeatureEnabled,
    };
};

export interface Actions {
    login?: (email: string, password: string, ipaddress: string) => Promise<Store.UserStore.Types.Authentication>;
    onNeedIpAddress?: () => Promise<string>;
}

export interface LoginPageProps {
    actions?: Actions;
}

export const LoginPage: React.FC<LoginPageProps> = ({ actions }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { from } = location.state || { from: { pathname: '/borrower' } };

    const queryParams = new URLSearchParams(location.search);
    const requestedEmail = queryParams.get('email');

    const [email, setEmail] = useState<string>(requestedEmail ?? '');
    const [password, setPassword] = useState<string>('');
    const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [attempts, setAttempts] = useState<number>(0);
    const [submitError, setSubmitError] = useState<string>();
    const [ipAddress, setIpAddress] = useState<string>('');

    const [redirectToReferrer] = useState<boolean>(false);
    const [redirectToHome, setRedirectToHome] = useState<boolean>(false);
    const [redirectToOther, setRedirectToOther] = useState(false);

    const { logout, login } = useAuthentication();

    const mounted = useRef<boolean>(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const handleKeypress = (e) => {
        if (email && password && !submitting) {
            if (e.key === 'Enter') {
                handleLogin();
            }
        }
    };

    const handleLogin = async () => {
        /* istanbul ignore if */
        if (!email || !password) {
            return;
        }
        setAttempts((prev) => prev + 1);

        setSubmitting(true);
        setError(false);
        setErrorMessage('');

        const result = await login(email, password, ipAddress, actions?.login);
        // istanbul ignore else
        if (mounted) {
            setSubmitting(false);
            if (result) {
                console.log('Log successful login to Application Insights.');
                applicationInsights.appInsights.trackEvent({
                    name: `${email} has logged in successfully.`,
                });

                const queryParams = new URLSearchParams(location.search);
                const redirectTo = queryParams.get('redirectTo');
                if (redirectTo == null) {
                    setRedirectToHome(true);
                } else {
                    setRedirectToOther(true);
                }
            } else {
                setError(true);
                setErrorMessage('email and/or password invalid');
            }
        }
    };

    useEffect(() => {
        let canceled = false;

        if (actions?.onNeedIpAddress) {
            actions.onNeedIpAddress().then((ip) => {
                if (!canceled) {
                    setIpAddress(ip);
                }
            });
        }

        return () => {
            canceled = true;
        };
    }, [actions]);

    useEffect(() => {
        logout();
    }, [logout]);

    useEffect(() => {
        if (submitError) {
            alertModal({
                message: 'An error has occurred while communicating with our server.  Please try your operation again.',
            });
            setSubmitError(undefined);
        }
    }, [submitError]);

    useEffect(() => {
        if (redirectToOther) {
            const queryParams = new URLSearchParams(location.search);
            const redirectTo = queryParams.get('redirectTo') || '';
            navigate(redirectTo);
        }

        if (redirectToReferrer) {
            navigate(from.pathname);
        }

        if (redirectToHome) {
            navigate('/home');
        }
    }, [redirectToOther, redirectToReferrer, redirectToHome]);

    return (
        <>
            <Header />

            <div className='login-banner'>
                <div className='welcome'>Let's get started!</div>
                <div className='direction'>New users please register.</div>
                <div className='rule'>&nbsp;</div>
            </div>

            <div className='app-container login apply-login-container'>
                <main>
                    <div className='simple-form-container one-column'>
                        <div className='columns form-content'>
                            <div className='standard-form'>
                                <div className='row'>
                                    <div className='columns small-12 large-12'>
                                        <label htmlFor='emailAddress'>
                                            Email Address
                                            <span className='req'>*</span>
                                        </label>
                                        <input
                                            id='emailAddress'
                                            data-testid='login-page-email-address'
                                            type='email'
                                            autoFocus
                                            autoComplete='email'
                                            value={email}
                                            onKeyPress={handleKeypress}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                        <span className='helper'>
                                            <span className='tooltip-trigger'>
                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                            </span>
                                            <span className='text'>Enter your email address, i.e. johnsmith@email.com</span>
                                        </span>
                                    </div>
                                    <div className='columns small-12 large-12'>
                                        <label htmlFor='password'>
                                            Password
                                            <span className='req'>*</span>
                                        </label>
                                        <div className='has-show-hide'>
                                            <input
                                                id='password'
                                                data-testid='login-page-password'
                                                type={passwordVisible ? 'text' : 'password'}
                                                value={password}
                                                onKeyPress={handleKeypress}
                                                onChange={(e) => setPassword(e.target.value)}
                                                name='password'
                                                required
                                            />
                                            <button
                                                type='button'
                                                data-testid='login-page-show-password'
                                                className='show-hide'
                                                onClick={() => setPasswordVisible((prev) => !prev)}
                                            >
                                                <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} />
                                            </button>
                                        </div>
                                        <span className='helper'>
                                            <span className='tooltip-trigger'>
                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                            </span>
                                            <span className='text'>Enter your password</span>
                                        </span>
                                    </div>
                                    {error && (
                                        <div className='columns small-12 large-12'>
                                            <Typography style={{ color: 'red' }}>{errorMessage}</Typography>
                                        </div>
                                    )}
                                    {attempts > 3 && (
                                        <div
                                            className='columns small-12 large-12'
                                            data-testid='login-page-max-attempts-message'
                                        >
                                            <Typography>
                                                Would you like to
                                                <MaterialLink
                                                    component={Link}
                                                    to={'/forgotpassword'}
                                                >
                                                    &nbsp;Reset your password?
                                                </MaterialLink>
                                            </Typography>
                                        </div>
                                    )}
                                    <div className='columns small-12 large-12'>
                                        <button
                                            data-testid='login-page-sign-in-button'
                                            disabled={!email || !password || submitting}
                                            onClick={handleLogin}
                                            className='button signin-button'
                                        >
                                            Sign In
                                        </button>
                                    </div>
                                    <div className='columns small-6 large-6'>
                                        <MaterialLink
                                            component={Link}
                                            to='/register'
                                        >
                                            Register
                                        </MaterialLink>
                                    </div>
                                    <div className='columns small-6 large-6 text-right'>
                                        <MaterialLink
                                            component={Link}
                                            to={`/forgotPassword?email=${email}`}
                                        >
                                            Can't log in?
                                        </MaterialLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            login: (email: string, password: string, ipaddress: string) => {
                dispatch(Store.BorrowerStore.Actions.reset());
                dispatch(Store.PropertiesStore.Actions.reset());
                dispatch(Store.FormsStore.Actions.reset());
                return dispatch(Store.CrossStore.login(email, password, ipaddress));
            },
            onNeedIpAddress: () => dispatch(Store.CrossStore.getIpAddress()),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(LoginPage);
